import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { Eye, EyeOff } from "lucide-react";

const HOUSE_NUMBERS = Array.from({ length: 44 }, (_, i) => String(i + 1));
const STREETS = ["JALAN 1/1","JALAN 1/2","JALAN 1/3","JALAN 1/4","JALAN 1/5","JALAN 1/6","JALAN 1/7","JALAN 1/8"];

export const STREET_INVITE_CODES = {
  "JALAN 1/1": "0JLN11PERCASAMY",
  "JALAN 1/2": "0JLN12PERCASAMY",
  "JALAN 1/3": "0JLN13PERCASAMY",
  "JALAN 1/4": "0JLN14PERCASAMY",
  "JALAN 1/5": "0JLN15PERCASAMY",
  "JALAN 1/6": "0JLN16PERCASAMY",
  "JALAN 1/7": "0JLN17PERCASAMY",
  "JALAN 1/8": "0JLN18PERCASAMY",
};

// House 1 + JALAN 1/1  ->  01-1-1
function generateUsername(unit, street) {
  if (!unit || !street) return "";
  return `${String(unit).padStart(2, "0")}-${street.replace("JALAN ", "").replace("/", "-")}`;
}

/** Force full name to ALL CAPITAL LETTERS */
function toUpperName(value) {
  return value.toUpperCase();
}

function reqLabel(text) {
  return (
    <>
      {text} <span style={{ color: "var(--danger)" }}>*</span>
    </>
  );
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    invitation_code: "",
    email: "",
    password: "",
    phone: "",
    unit: "",
    street_number: "",
  });
  const [pdpaConsent, setPdpaConsent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const username = generateUsername(form.unit, form.street_number);

  function update(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setSuccess("");

    const name = toUpperName(form.name.trim());
    const invitation_code = form.invitation_code.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const password = form.password;

    if (!name) { setError("Please enter your full name."); return; }
    if (name !== form.name.trim()) setForm(f => ({ ...f, name }));
    if (!invitation_code) { setError("Please enter your street invitation code."); return; }
    if (!form.unit) { setError("Please select a house number."); return; }
    if (!form.street_number) { setError("Please select a street."); return; }
    if (!email) { setError("Please enter your email address."); return; }
    if (!phone) { setError("Please enter your phone number."); return; }
    if (!password) { setError("Please enter a password."); return; }

    const expected = STREET_INVITE_CODES[form.street_number];
    if (!expected || invitation_code.toUpperCase() !== expected) {
      setError("Invalid invitation code for the selected street. Please check with your JKKK / committee.");
      return;
    }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (!pdpaConsent) { setError("Please accept the PDPA consent declaration to continue."); return; }

    setLoading(true);
    try {
      const res = await register({
        name,
        email,
        password,
        phone,
        unit: form.unit,
        street_number: form.street_number,
        invitation_code: invitation_code.toUpperCase(),
        pdpa_consent: true,
      });
      if (res.status === "active") {
        setSuccess(`Registration successful! Your username is ${res.username || username}. You can now sign in.`);
        setTimeout(() => navigate("/login"), 3000);
      } else if (res.status === "pending") {
        setSuccess(`Registration submitted! Your username is ${res.username || username}. Please wait for admin approval before you can log in.`);
        setTimeout(() => navigate("/login"), 5000);
      } else {
        navigate("/");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-panel-left">
        <div className="auth-brand">
          <BrandLogo variant="auth" />
          <div className="auth-brand-desc">
            Create your resident account and connect with your building's management system.
          </div>
        </div>
      </div>

      <div className="auth-panel-right">
        <div className="auth-form-title">First-time login</div>
        <div className="auth-form-sub">Set up your PERCASA resident account. All fields are required.</div>

        {error   && <div className="auth-error"   data-testid="register-error">{error}</div>}
        {success && <div className="auth-success" data-testid="register-success">{success}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label className="auth-label">{reqLabel("Full name")}</label>
            <input
              className="auth-input"
              placeholder="AHMAD HISHAM"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: toUpperName(e.target.value) }))}
              autoCapitalize="characters"
              style={{ textTransform: "uppercase" }}
              required
              data-testid="register-name-input"
            />
            <div style={{ fontSize: 10, color: "var(--text2)", marginTop: 4 }}>
              Full name must be in capital letters (e.g. AHMAD HISHAM).
            </div>
          </div>
          <div className="auth-field">
            <label className="auth-label">{reqLabel("Invitation Code")}</label>
            <input
              className="auth-input"
              placeholder="Enter your street invitation code"
              value={form.invitation_code}
              onChange={update("invitation_code")}
              required
              autoCapitalize="characters"
              data-testid="register-invite-input"
            />
            <div style={{ fontSize: 10, color: "var(--text2)", marginTop: 4 }}>
              Provided by your street committee to confirm you belong to the correct Jalan.
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="auth-field">
              <label className="auth-label">{reqLabel("House Number")}</label>
              <select className="auth-input" value={form.unit} onChange={update("unit")} required data-testid="register-unit-input">
                <option value="">— Select —</option>
                {HOUSE_NUMBERS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="auth-field">
              <label className="auth-label">{reqLabel("Street")}</label>
              <select className="auth-input" value={form.street_number} onChange={update("street_number")} required data-testid="register-street-input">
                <option value="">— Select —</option>
                {STREETS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="auth-field">
            <label className="auth-label">Username (auto-generated)</label>
            <input className="auth-input" value={username} readOnly disabled
              placeholder="Select house number & street above"
              style={{ background: "var(--bg)", color: username ? "var(--brand-dark)" : "var(--text2)", fontWeight: username ? 700 : 400, letterSpacing: username ? 1 : 0, cursor: "not-allowed" }}
              data-testid="register-username-display" />
            {username && (
              <div style={{ fontSize: 10, color: "var(--text2)", marginTop: 4 }}>
                This will be your login username. Please remember it.
              </div>
            )}
          </div>
          <div className="auth-field">
            <label className="auth-label">{reqLabel("Email Address")}</label>
            <input type="email" className="auth-input" placeholder="ahmad@example.com" value={form.email} onChange={update("email")} required data-testid="register-email-input" />
          </div>
          <div className="auth-field">
            <label className="auth-label">{reqLabel("Phone Number")}</label>
            <input className="auth-input" placeholder="+60 12-345 6789" value={form.phone} onChange={update("phone")} required data-testid="register-phone-input" />
          </div>
          <div className="auth-field">
            <label className="auth-label">{reqLabel("Password")}</label>
            <div style={{ position: "relative" }}>
              <input type={showPw ? "text" : "password"} className="auth-input" placeholder="Min. 6 characters"
                value={form.password} onChange={update("password")} required
                style={{ paddingRight: 38, width: "100%" }} data-testid="register-password-input" />
              <button type="button" onClick={() => setShowPw(s => !s)} title={showPw ? "Hide password" : "Show password"}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text2)", padding: 0, display: "flex" }}
                data-testid="register-toggle-password">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div className="auth-field pdpa-consent" data-testid="pdpa-consent">
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", fontSize: 12, color: "var(--text)", lineHeight: 1.5 }}>
              <input
                type="checkbox"
                checked={pdpaConsent}
                onChange={e => setPdpaConsent(e.target.checked)}
                required
                style={{ marginTop: 3, accentColor: "var(--brand-dark)" }}
                data-testid="pdpa-consent-checkbox"
              />
              <span>
                I have read and agree to the{" "}
                <Link
                  to="/pdpa"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--brand-dark)", fontWeight: 600, textDecoration: "underline" }}
                  data-testid="pdpa-notice-link"
                  onClick={e => e.stopPropagation()}
                >
                  PDPA Notice
                </Link>
                {" "}
                and consent to the processing of my personal data by PERCASA Residence Management
                in accordance with the Personal Data Protection Act 2010 (PDPA).
                <span style={{ color: "var(--danger)" }}> *</span>
              </span>
            </label>
          </div>

          <button type="submit" className="auth-submit" disabled={loading || !pdpaConsent} data-testid="register-submit-btn">
            {loading ? "Registering..." : "Register"}
          </button>
        </form>

        <div className="auth-link">
          Already have an account?{" "}
          <Link to="/login" data-testid="login-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
